import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiDelete } from '@/utils/api';
import toast from 'react-hot-toast';

interface ConsentStatus {
  consentType: string;
  hasConsented: boolean;
  version?: string;
  consentedAt?: string;
  withdrawnAt?: string;
}

interface UserConsentsResponse {
  userId: string;
  userRole: string;
  consents: ConsentStatus[];
  hasAllRequiredConsents: boolean;
  missingRequiredConsents: string[];
  requiredConsents: string[];
  permissions: {
    canExportData: boolean;
    canRequestDeletion: boolean;
  };
}

interface DataExportRequest {
  id: string;
  status: string;
  format: string;
  downloadUrl?: string;
  expiresAt?: string;
  requestedAt: string;
  processedAt?: string;
}

interface AccountDeletionRequest {
  id: string;
  status: string;
  reason?: string;
  requestedAt: string;
  scheduledFor?: string;
  rejectionReason?: string;
}

const CONSENT_LABELS: Record<string, string> = {
  PRIVACY_POLICY: 'Politique de Confidentialité',
  TERMS_OF_SERVICE: "Conditions d'Utilisation",
  DATA_PROCESSING: 'Traitement des Données',
  MARKETING_COMMUNICATIONS: 'Communications Marketing',
  // Legacy types kept for displaying any historic acceptance records but no
  // longer collected via CNDP. Now signed via institutional documents flow.
  MEDIA_CONSENT: 'Consentement Médias (institutionnel)',
  INTERNAL_REGULATIONS_STAFF: 'Règlement Intérieur Personnel (institutionnel)',
  INTERNAL_REGULATIONS_STUDENT: 'Règlement Intérieur Étudiant (institutionnel)',
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  EXPORT_PENDING: { label: 'En attente', color: 'text-yellow-600 bg-yellow-50' },
  EXPORT_PROCESSING: { label: 'En cours', color: 'text-blue-600 bg-blue-50' },
  EXPORT_READY: { label: 'Prêt', color: 'text-green-600 bg-green-50' },
  EXPORT_DOWNLOADED: { label: 'Téléchargé', color: 'text-gray-600 bg-gray-50' },
  EXPORT_EXPIRED: { label: 'Expiré', color: 'text-red-600 bg-red-50' },
  EXPORT_FAILED: { label: 'Échoué', color: 'text-red-600 bg-red-50' },
  DELETION_PENDING: { label: 'En attente de validation', color: 'text-yellow-600 bg-yellow-50' },
  DELETION_APPROVED: { label: 'Approuvé', color: 'text-green-600 bg-green-50' },
  DELETION_SCHEDULED: { label: 'Programmé', color: 'text-orange-600 bg-orange-50' },
  DELETION_COMPLETED: { label: 'Complété', color: 'text-gray-600 bg-gray-50' },
  DELETION_REJECTED: { label: 'Refusé', color: 'text-red-600 bg-red-50' },
  DELETION_CANCELLED: { label: 'Annulé', color: 'text-gray-600 bg-gray-50' },
};

export const PrivacySettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [consents, setConsents] = useState<UserConsentsResponse | null>(null);
  const [dataExports, setDataExports] = useState<DataExportRequest[]>([]);
  const [deletionRequests, setDeletionRequests] = useState<AccountDeletionRequest[]>([]);
  const [exporting, setExporting] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [submittingDeletion, setSubmittingDeletion] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [consentsRes, exportsRes, deletionsRes] = await Promise.all([
        apiGet<UserConsentsResponse>('/cndp/consent/status'),
        apiGet<DataExportRequest[]>('/cndp/data-export'),
        apiGet<AccountDeletionRequest[]>('/cndp/account-deletion'),
      ]);
      setConsents(consentsRes);
      setDataExports(exportsRes);
      setDeletionRequests(deletionsRes);
    } catch (err) {
      console.error('Failed to load privacy data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawConsent = async (consentType: string) => {
    // Check if this is a required consent
    const isRequired = consents?.requiredConsents?.includes(consentType);

    const confirmMessage = isRequired
      ? `Attention : "${CONSENT_LABELS[consentType] || consentType}" est un consentement obligatoire. Si vous le retirez, vous devrez le re-signer pour continuer à utiliser la plateforme. Êtes-vous sûr ?`
      : `Êtes-vous sûr de vouloir retirer votre consentement pour "${CONSENT_LABELS[consentType] || consentType}" ?`;

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      await apiDelete(`/cndp/consent/${consentType}`);
      toast.success('Consentement retiré avec succès');

      // If it was a required consent, reload the page to show the consent modal
      if (isRequired) {
        window.location.reload();
      } else {
        await loadAllData();
      }
    } catch (err) {
      console.error('Failed to withdraw consent:', err);
      toast.error('Erreur lors du retrait du consentement');
    }
  };

  const handleRequestDataExport = async (format: 'JSON' | 'PDF' | 'CSV' = 'JSON') => {
    try {
      setExporting(true);
      await apiPost('/cndp/data-export', { format });
      await loadAllData();
      toast.success('Votre demande d\'export a été enregistrée. Vous recevrez une notification lorsque vos données seront prêtes.');
    } catch (err: unknown) {
      console.error('Failed to request data export:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la demande d\'export.';
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadExport = async (request: DataExportRequest) => {
    if (!request.downloadUrl) return;

    try {
      // Mark as downloaded
      await apiPost(`/cndp/data-export/${request.id}/downloaded`, {});

      // Download the file
      const link = document.createElement('a');
      link.href = request.downloadUrl;
      link.download = `mes-donnees-${new Date().toISOString().split('T')[0]}.${request.format.toLowerCase()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      await loadAllData();
    } catch (err) {
      console.error('Failed to download export:', err);
    }
  };

  const handleRequestDeletion = async () => {
    try {
      setSubmittingDeletion(true);
      await apiPost('/cndp/account-deletion', { reason: deletionReason || undefined });
      setShowDeletionModal(false);
      setDeletionReason('');
      await loadAllData();
      toast.success('Votre demande de suppression a été enregistrée. Elle sera examinée par un administrateur.');
    } catch (err: unknown) {
      console.error('Failed to request deletion:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la demande de suppression.';
      toast.error(errorMessage);
    } finally {
      setSubmittingDeletion(false);
    }
  };

  const handleCancelDeletion = async (requestId: string) => {
    try {
      await apiDelete(`/cndp/account-deletion/${requestId}`);
      await loadAllData();
      toast.success('Demande de suppression annulée');
    } catch (err) {
      console.error('Failed to cancel deletion:', err);
      toast.error('Erreur lors de l\'annulation de la demande');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const pendingDeletion = deletionRequests.find(
    (r) => ['DELETION_PENDING', 'DELETION_APPROVED', 'DELETION_SCHEDULED'].includes(r.status)
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Paramètres de Confidentialité</h2>
        <p className="mt-1 text-gray-500">
          Gérez vos consentements et vos données personnelles conformément à la CNDP.
        </p>
      </div>

      {/* Consents Section */}
      <section className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">Mes Consentements</h3>
          <p className="text-sm text-gray-500">Gérez vos préférences de consentement</p>
        </div>
        <div className="divide-y">
          {consents?.consents.map((consent) => (
            <div key={consent.consentType} className="px-6 py-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">
                  {CONSENT_LABELS[consent.consentType] || consent.consentType}
                </p>
                {consent.hasConsented && consent.consentedAt && (
                  <p className="text-sm text-gray-500">
                    Accepté le {new Date(consent.consentedAt).toLocaleDateString('fr-FR')}
                    {consent.version && ` (v${consent.version})`}
                  </p>
                )}
                {!consent.hasConsented && consent.withdrawnAt && (
                  <p className="text-sm text-red-500">
                    Retiré le {new Date(consent.withdrawnAt).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {consent.hasConsented ? (
                  <>
                    <span className="text-green-600 text-sm font-medium">Actif</span>
                    <button
                      onClick={() => handleWithdrawConsent(consent.consentType)}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Retirer
                    </button>
                  </>
                ) : (
                  <span className="text-gray-400 text-sm">Non consenti</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Data Export Section - Only for staff and parents */}
      {consents?.permissions?.canExportData && (
        <section className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">Export de mes Données</h3>
            <p className="text-sm text-gray-500">
              Téléchargez une copie de toutes vos données personnelles
            </p>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => handleRequestDataExport('JSON')}
                disabled={exporting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {exporting ? 'Demande en cours...' : 'Demander un export (JSON)'}
              </button>
              <button
                onClick={() => handleRequestDataExport('CSV')}
                disabled={exporting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
              >
                Export CSV
              </button>
            </div>

            {dataExports.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Historique des exports</h4>
                <div className="space-y-2">
                  {dataExports.slice(0, 5).map((exp) => (
                    <div
                      key={exp.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            STATUS_LABELS[exp.status]?.color || 'text-gray-600 bg-gray-100'
                          }`}
                        >
                          {STATUS_LABELS[exp.status]?.label || exp.status}
                        </span>
                        <span className="text-sm text-gray-600">
                          {new Date(exp.requestedAt).toLocaleDateString('fr-FR')} - {exp.format}
                        </span>
                      </div>
                      {exp.status === 'EXPORT_READY' && exp.downloadUrl && (
                        <button
                          onClick={() => handleDownloadExport(exp)}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Télécharger
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Account Deletion Section - Only for staff and parents */}
      {consents?.permissions?.canRequestDeletion && (
        <section className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b bg-red-50">
            <h3 className="text-lg font-semibold text-red-900">Suppression du Compte</h3>
            <p className="text-sm text-red-700">
              Demandez la suppression définitive de votre compte et de vos données
            </p>
          </div>
          <div className="p-6">
            {pendingDeletion ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-yellow-600 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div className="flex-1">
                    <p className="font-medium text-yellow-800">
                      Demande de suppression en cours
                    </p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Statut: {STATUS_LABELS[pendingDeletion.status]?.label || pendingDeletion.status}
                      {pendingDeletion.scheduledFor && (
                        <>
                          {' '}
                          - Programmée pour le{' '}
                          {new Date(pendingDeletion.scheduledFor).toLocaleDateString('fr-FR')}
                        </>
                      )}
                    </p>
                    <button
                      onClick={() => handleCancelDeletion(pendingDeletion.id)}
                      className="mt-3 text-sm text-yellow-700 hover:text-yellow-800 font-medium underline"
                    >
                      Annuler la demande
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-4">
                  En demandant la suppression de votre compte, toutes vos données personnelles seront
                  définitivement effacées après validation par un administrateur. Cette action est
                  irréversible.
                </p>
                <button
                  onClick={() => setShowDeletionModal(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Demander la suppression de mon compte
                </button>
              </>
            )}

            {/* Previous deletion requests */}
            {deletionRequests.filter(r => !['DELETION_PENDING', 'DELETION_APPROVED', 'DELETION_SCHEDULED'].includes(r.status)).length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Historique des demandes</h4>
                <div className="space-y-2">
                  {deletionRequests
                    .filter(r => !['DELETION_PENDING', 'DELETION_APPROVED', 'DELETION_SCHEDULED'].includes(r.status))
                    .slice(0, 3)
                    .map((req) => (
                      <div
                        key={req.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              STATUS_LABELS[req.status]?.color || 'text-gray-600 bg-gray-100'
                            }`}
                          >
                            {STATUS_LABELS[req.status]?.label || req.status}
                          </span>
                          <span className="text-sm text-gray-600">
                            {new Date(req.requestedAt).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        {req.rejectionReason && (
                          <span className="text-sm text-red-600">{req.rejectionReason}</span>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Deletion Confirmation Modal */}
      {showDeletionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Confirmer la suppression du compte
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Êtes-vous sûr de vouloir supprimer votre compte ? Cette action entraînera la
                suppression définitive de toutes vos données après validation.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raison (optionnel)
                </label>
                <textarea
                  value={deletionReason}
                  onChange={(e) => setDeletionReason(e.target.value)}
                  rows={3}
                  placeholder="Pourquoi souhaitez-vous supprimer votre compte ?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeletionModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={handleRequestDeletion}
                  disabled={submittingDeletion}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
                >
                  {submittingDeletion ? 'Envoi...' : 'Confirmer la suppression'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacySettings;
